/**
 * Hub Board & management (HW-9) — refresh the current board each hub shows.
 * Rows come from the same fold as the entity Directors panel, and only rows
 * that pass every check are written (company's own filing, name and role in
 * its text, within 18 months, no later resignation, no open duplicate).
 */
import { useState } from 'react';
import { LayoutList, Eye, Play, Loader2 } from 'lucide-react';
import { runHubBoardRefresh, type HubBoardResult } from '../lib/api/hub-board.js';

export function HubBoardPanel() {
  const [running, setRunning] = useState<'dry' | 'apply' | null>(null);
  const [result, setResult] = useState<HubBoardResult | null>(null);
  const [mode, setMode] = useState<'dry' | 'apply'>('dry');
  const [codes, setCodes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | undefined>();

  const run = async (m: 'dry' | 'apply') => {
    if (m === 'apply' && !window.confirm(`Write the hub board for ${codes.trim() || 'every company'}?\n\nRun the dry run first to see the counts.`)) return;
    setRunning(m); setError(null); setDone(undefined);
    try {
      const r = await runHubBoardRefresh(m === 'dry', codes.trim() || undefined, st => setDone(st.progress?.done));
      setResult(r); setMode(m);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally { setRunning(null); }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-grey-500"><LayoutList className="h-5 w-5" /></div>
        <div className="flex-1">
          <h3 className="font-semibold text-grey-900">Hub board &amp; management</h3>
          <p className="text-sm text-grey-600 mt-1">
            The current board, the last 12 months of director dealings and the current substantial holders each company
            hub shows. A row is listed only when the company&apos;s own filing names the person or holder (and the role),
            from its own text. Runs nightly; refresh here after merging people.
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={codes} onChange={e => setCodes(e.target.value)} placeholder="Codes, e.g. AQN,MEM (blank = all)"
          className="px-3 py-1.5 text-sm border border-grey-300 rounded w-64 max-w-full" />
        <button onClick={() => run('dry')} disabled={!!running}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50">
          {running === 'dry' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Dry run
        </button>
        <button onClick={() => run('apply')} disabled={!!running}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
          {running === 'apply' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Write
        </button>
      </div>
      {running && <p className="mt-2 text-xs text-grey-500">Running{done ? ` — ${done} companies so far` : ''}…</p>}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      {result && (
        <div className="mt-3 rounded bg-grey-50 px-3 py-2 text-xs text-grey-700 space-y-1">
          {result.error ? <div className="text-red-700">{result.error}</div> : (
            <>
              <div>
                <span className="font-medium text-grey-800">{mode === 'dry' ? 'Dry run' : 'Written'}:</span>{' '}
                {result.rows} people across {result.entities_with_rows} of {result.entities} companies
                {result.mixed_record_skipped ? ` · ${result.mixed_record_skipped} mixed records skipped` : ''}
                {result.removed_out_of_scope ? ` · ${result.removed_out_of_scope} out-of-scope rows removed` : ''}
                {` · older than ${result.stale_cutoff} not shown`}
              </div>
              {Object.keys(result.rejected).length > 0 && (
                <div className="text-grey-500">Not listed: {Object.entries(result.rejected).map(([k, v]) => `${k} ${v}`).join(' · ')}</div>
              )}
              {(['dealings', 'holders'] as const).map(b => result[b] && (
                <div key={b}>
                  <span className="font-medium text-grey-800">{b === 'dealings' ? 'Director dealings' : 'Substantial holders'}:</span>{' '}
                  {result[b]!.rows} across {result[b]!.entities} companies
                  {Object.keys(result[b]!.rejected).length > 0 &&
                    <span className="text-grey-500"> · not listed: {Object.entries(result[b]!.rejected).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>}
                </div>
              ))}
              {Object.entries(result.samples).map(([code, s]) => (
                <div key={code} className="pt-1">
                  <div><span className="font-medium text-grey-800">{code} board:</span>{' '}
                    {s.board.map(r => `${r.display_name} — ${r.role_label}${r.since ? ` since ${r.since}` : ''}`).join('; ') || 'nobody listed'}</div>
                  <div><span className="font-medium text-grey-800">{code} dealings:</span>{' '}
                    {s.dealings.map(d => `${d.date_is_notified ? `notified ${d.notified_date}` : d.trade_date} ${d.display_name} ${d.label}${d.shares != null ? ` ${Number(d.shares).toLocaleString()} sh` : ''}${d.value_aud != null ? ` A$${Number(d.value_aud).toLocaleString()}` : ''}`).join('; ') || 'none'}</div>
                  <div><span className="font-medium text-grey-800">{code} holders:</span>{' '}
                    {s.holders.map(h => `${h.holder_name_as_filed} ${h.pct}%`).join('; ') || 'none'}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
